import type { Photo } from "@/core/photo"
import type { Mask, PeopleMask, PeopleMaskPart } from "@/core/types"
import { clamp } from "@/core/utils/math"
import { fitToSize } from "@/core/utils/resize-image"
import { toNoOrientation } from "@/lib/ai/alignment/ai-image-align"
import type { AIMattingDetection } from "@/lib/ai/detection/ai-detection"
import type { Rect } from "@/lib/ai/detection/face-detection"
import type {
	RfDetrPeopleSegmentationResult,
	RfDetrSegmentedPerson,
} from "@/lib/ai/detection/rfdetr-person-segmentation"
import {
	createSchpPartMask,
	projectSchpPartMask,
	type SchpHumanParsingPart,
	type SchpHumanParsingResult,
} from "@/lib/ai/detection/schp-human-parser"
import {
	createFaceParsingPartMask,
	type FaceParsingPart,
	type FaceParsingResult,
	getFaceParsingSourceRect,
	projectFaceParsingPartMask,
} from "@/lib/ai/detection/uniface-face-parser"
import {
	faceDetectorConfidenceThresholdAtom,
	faceParserConfidenceThresholdAtom,
	rfdetrPersonConfidenceThresholdAtom,
} from "@/store/settings-state"
import { store } from "@/store/store"
import type {
	AIMaskArtifactBundleRecord,
	AIMaskArtifactChunkRecord,
} from "@/storage/ai-mask-artifact-types"
import { Sentry } from "@/telemetry/sentry"
import { AbortError } from "@/utils/error"
import { isRetouchingEnabled } from "@/utils/measurement/feature-flag"
import type { Atom } from "jotai"
import { mat4, vec2, vec4 } from "gl-matrix"
import type { MaskCache } from "@/storage/mask-cache"
import {
	AIMaskNotDetectedError,
	canonicalizeAlphaMask,
	createArtifactChunk,
	decodeAlphaMask,
	getArtifactDescriptor,
	type MaskResult,
	type RenderMaskAnalysisInput,
	writeArtifact,
} from "./ai-mask-result"

export interface SegmentedPerson extends RfDetrSegmentedPerson {
	thumbnailUrl?: string
}

export interface PeopleSegmentationResult extends Omit<RfDetrPeopleSegmentationResult, "persons"> {
	persons: SegmentedPerson[]
}

export type VisibleSegmentedPerson = Omit<SegmentedPerson, "mask">

export interface VisiblePeopleSegmentationResult extends Omit<PeopleSegmentationResult, "persons"> {
	persons: VisibleSegmentedPerson[]
}

interface StoredPeopleSegmentationMetadata {
	imageSpace: RfDetrPeopleSegmentationResult["imageSpace"]
	persons: Array<Omit<RfDetrSegmentedPerson, "mask">>
	faces: RfDetrPeopleSegmentationResult["faces"]
	faceMatches: RfDetrPeopleSegmentationResult["faceMatches"]
}

export type PeopleMaskPartAvailability = Record<Exclude<PeopleMaskPart, "entire">, boolean>

export interface AIPeopleMaskService {
	readonly maskDetectionCoords: Readonly<Record<string, vec2>>
	getVisiblePeopleSegmentation(photo: Photo): Promise<VisiblePeopleSegmentationResult | null>
	getPeopleMaskPartAvailability(
		photo: Photo,
		personIndexes: number[],
	): Promise<PeopleMaskPartAvailability>
}

const faceAvailabilityParts = ["eyebrows", "eyes", "lips"] as const
const humanAvailabilityParts = ["facialSkin", "bodySkin", "hair", "clothing"] as const

type PeopleMaskProxy = Pick<
	AIMattingDetection,
	"getPeopleSegmentation" | "getFaceParsing" | "getHumanParsing"
>

export class AIPeopleMaskProvider implements AIPeopleMaskService {
	public maskDetectionCoords: Record<string, vec2> = {}

	private inFlightPeopleSegmentations = new Map<
		string,
		Promise<PeopleSegmentationResult | null>
	>()
	private inFlightFaceParsing = new Map<string, Promise<FaceParsingResult>>()
	private inFlightHumanParsing = new Map<string, Promise<SchpHumanParsingResult>>()
	private peopleSegmentationInput: ImageBitmap | null = null
	private faceParsingInput: ImageBitmap | null = null
	private generation = 0

	public constructor(
		private readonly proxy: PeopleMaskProxy,
		private readonly renderAnalysisInput: RenderMaskAnalysisInput,
		private readonly cache?: MaskCache,
		private readonly onDetectionCoordinatesChanged?: (mask: Mask) => void,
	) {}

	public cleanup() {
		this.generation += 1
		this.inFlightPeopleSegmentations.clear()
		this.inFlightFaceParsing.clear()
		this.inFlightHumanParsing.clear()
		this.maskDetectionCoords = {}

		this.peopleSegmentationInput?.close()
		this.peopleSegmentationInput = null
		this.faceParsingInput?.close()
		this.faceParsingInput = null
	}

	public async getMaskResult(
		photo: Photo,
		mask: PeopleMask,
		signal: AbortSignal,
	): Promise<MaskResult> {
		if (!isRetouchingEnabled()) {
			throw new Error("Retouching is disabled")
		}

		const personIndexes = getMaskPersonIndexes(mask)
		const peoplePart = mask.peoplePart ?? "entire"
		const result = await this.getPeopleMaskResult(photo, personIndexes, peoplePart)

		signal.throwIfAborted()

		if (!result) {
			throw new AIMaskNotDetectedError("Could not detect people")
		}

		await this.setPeopleMaskDetectionCoord(photo, mask, personIndexes, result)

		return result
	}

	public getMaskTextureId(photo: Photo, mask: PeopleMask) {
		return `${this.getPeopleSegmentationId(photo)}_${getPeopleMaskSelectionKey(mask)}`
	}

	public async getPeopleSegmentation(photo: Photo): Promise<PeopleSegmentationResult | null> {
		if (!isRetouchingEnabled()) {
			return null
		}

		const id = this.getPeopleSegmentationId(photo)
		const cached = this.inFlightPeopleSegmentations.get(id)
		if (cached) {
			return cached
		}

		const generation = this.generation
		const promise = this.computePeopleSegmentation(photo, generation).finally(() => {
			if (this.inFlightPeopleSegmentations.get(id) === promise) {
				this.inFlightPeopleSegmentations.delete(id)
			}
		})
		this.inFlightPeopleSegmentations.set(id, promise)
		return promise
	}

	public async getVisiblePeopleSegmentation(
		photo: Photo,
	): Promise<VisiblePeopleSegmentationResult | null> {
		if (!isRetouchingEnabled()) {
			return null
		}
		const generation = this.generation
		const stored = await this.readStoredPeopleSegmentationMetadata(photo)
		this.assertCurrentGeneration(generation)
		if (!stored) {
			const segmentation = await this.getPeopleSegmentation(photo)
			this.assertCurrentGeneration(generation)
			const visible = segmentation
				? await this.createVisiblePeopleSegmentation(photo, segmentation)
				: null
			this.assertCurrentGeneration(generation)
			return visible
		}

		const { bundle, metadata } = stored
		await this.precomputePeoplePartsFromMetadata(photo, metadata, generation)
		this.assertCurrentGeneration(generation)
		const input = await this.getPeopleSegmentationInput(photo)
		this.assertCurrentGeneration(generation)
		const persons: VisibleSegmentedPerson[] = []
		for (const person of metadata.persons) {
			this.assertCurrentGeneration(generation)
			// Instance chunks are loaded one at a time so visibility filtering never retains every
			// full-size person mask in CPU memory.
			const storedPerson = await this.cache?.readArtifactBundle(bundle.id, [
				`person:${person.personIndex}`,
			])
			this.assertCurrentGeneration(generation)
			const chunk = storedPerson?.chunks[0]
			if (!chunk) {
				const segmentation = await this.getPeopleSegmentation(photo)
				this.assertCurrentGeneration(generation)
				const visible = segmentation
					? await this.createVisiblePeopleSegmentation(photo, segmentation)
					: null
				this.assertCurrentGeneration(generation)
				return visible
			}
			if (
				!maskIntersectsVisibleImage(
					decodeAlphaMask(chunk).mask,
					metadata.imageSpace.width,
					metadata.imageSpace.height,
					photo.imageState.cropMatrixInv,
					photo.initialOrientation,
				)
			) {
				continue
			}
			const face =
				person.matchedFaceIndex === undefined
					? undefined
					: metadata.faces[person.matchedFaceIndex]
			persons.push({
				...person,
				thumbnailUrl: input
					? this.createPeopleThumbnail(input, person, face?.faceRect)
					: undefined,
			})
		}

		return {
			...metadata,
			persons,
		}
	}

	public async getPeopleMaskPartAvailability(
		photo: Photo,
		personIndexes: number[],
	): Promise<PeopleMaskPartAvailability> {
		const availability = createEmptyPeopleMaskPartAvailability()
		if (!isRetouchingEnabled()) {
			return availability
		}
		const generation = this.generation
		const stored = await this.readStoredPeopleSegmentationMetadata(photo)
		this.assertCurrentGeneration(generation)
		const segmentation = stored
			? ({
					...stored.metadata,
					persons: stored.metadata.persons.map((person) => ({
						...person,
						mask: new Float32Array(0),
					})),
				} satisfies PeopleSegmentationResult)
			: await this.getPeopleSegmentation(photo)
		this.assertCurrentGeneration(generation)
		if (!segmentation || personIndexes.length === 0) {
			return availability
		}

		const selectedInstances = personIndexes
			.map((personIndex) =>
				segmentation.persons.find((item) => item.personIndex === personIndex),
			)
			.filter((instance) => instance !== undefined)

		for (const instance of selectedInstances) {
			this.assertCurrentGeneration(generation)
			const humanParsing = await this.getHumanParsing(photo, instance)
			this.assertCurrentGeneration(generation)
			for (const part of humanAvailabilityParts) {
				if (!availability[part]) {
					availability[part] = hasMaskPixels(
						createSchpPartMask(humanParsing.classMask, part),
					)
				}
			}

			if (instance.matchedFaceIndex === undefined) {
				continue
			}

			const face = segmentation.faces[instance.matchedFaceIndex]
			if (!face) {
				continue
			}

			const faceParsing = await this.getFaceParsing(
				photo,
				instance.personIndex,
				getFaceParsingSourceRect(face),
			)
			this.assertCurrentGeneration(generation)

			for (const part of faceAvailabilityParts) {
				if (!availability[part]) {
					availability[part] = hasMaskPixels(
						createFaceParsingPartMask(faceParsing.classMask, part),
					)
				}
			}
		}

		return availability
	}

	private assertCurrentGeneration(generation: number) {
		if (generation !== this.generation) {
			throw new AbortError("People segmentation belongs to a previous photo")
		}
	}

	private async readStoredPeopleSegmentationMetadata(photo: Photo) {
		const descriptor = this.getPeopleSegmentationDescriptor(photo)
		const bundle = descriptor ? await this.cache?.readArtifactMetadata(descriptor.id) : null
		if (!bundle) {
			return null
		}
		if (!isStoredPeopleSegmentationMetadata(bundle.metadata, bundle.chunkRoles)) {
			// IndexedDB is disposable cache storage. Invalid metadata must behave like a miss so
			// inference can rebuild the complete bundle instead of becoming a permanent null result.
			await this.cache?.deleteArtifact(bundle.id).catch(Sentry.captureException)
			return null
		}
		return { bundle, metadata: bundle.metadata }
	}

	private async createVisiblePeopleSegmentation(
		photo: Photo,
		segmentation: PeopleSegmentationResult,
	) {
		const input = await this.getPeopleSegmentationInput(photo)
		return {
			imageSpace: segmentation.imageSpace,
			faces: segmentation.faces,
			faceMatches: segmentation.faceMatches,
			persons: segmentation.persons
				.filter((person) =>
					maskIntersectsVisibleImage(
						person.mask,
						segmentation.imageSpace.width,
						segmentation.imageSpace.height,
						photo.imageState.cropMatrixInv,
						photo.initialOrientation,
					),
				)
				.map(({ mask: _mask, ...person }) => {
					const face =
						person.matchedFaceIndex === undefined
							? undefined
							: segmentation.faces[person.matchedFaceIndex]
					return {
						...person,
						thumbnailUrl: input
							? this.createPeopleThumbnail(input, person, face?.faceRect)
							: undefined,
					}
				}),
		}
	}

	private async getPeopleMaskResult(
		photo: Photo,
		personIndexes: number[] | undefined,
		peoplePart: PeopleMaskPart = "entire",
	) {
		const generation = this.generation
		const stored = await this.readStoredPeopleSegmentationMetadata(photo)
		this.assertCurrentGeneration(generation)
		if (stored) {
			const selectedPersonIndexes = getSelectedPersonIndexes(
				stored.metadata,
				personIndexes,
				peoplePart,
			)
			if (selectedPersonIndexes.length === 0) {
				return null
			}

			const result = {
				mask: new Float32Array(
					stored.metadata.imageSpace.width * stored.metadata.imageSpace.height,
				),
				width: stored.metadata.imageSpace.width,
				height: stored.metadata.imageSpace.height,
			}
			let matchedPerson = false
			let cacheIncomplete = false
			for (const personIndex of selectedPersonIndexes) {
				this.assertCurrentGeneration(generation)
				const person = stored.metadata.persons.find(
					(item) => item.personIndex === personIndex,
				)
				if (!person) {
					continue
				}
				const storedPerson = await this.cache?.readArtifactBundle(stored.bundle.id, [
					`person:${personIndex}`,
				])
				this.assertCurrentGeneration(generation)
				const chunk = storedPerson?.chunks[0]
				if (!chunk) {
					// A selective read deletes incomplete bundles. Discard any partial composition
					// and rebuild from inference below.
					cacheIncomplete = true
					break
				}

				matchedPerson = true
				const instance = { ...person, mask: decodeAlphaMask(chunk).mask }
				const segmentation = {
					...stored.metadata,
					persons: [instance],
				} satisfies PeopleSegmentationResult
				const instanceMask = await this.getPeoplePartMask(
					photo,
					segmentation,
					instance,
					peoplePart,
				)
				this.assertCurrentGeneration(generation)
				if (instanceMask) {
					mergeMask(result.mask, instanceMask)
				}
			}
			if (!cacheIncomplete) {
				return matchedPerson ? result : null
			}
		}

		const segmentation = await this.getPeopleSegmentation(photo)
		this.assertCurrentGeneration(generation)
		if (!segmentation) {
			return null
		}
		return await this.composePeopleMaskFromSegmentation(
			photo,
			segmentation,
			personIndexes,
			peoplePart,
			generation,
		)
	}

	private async composePeopleMaskFromSegmentation(
		photo: Photo,
		segmentation: PeopleSegmentationResult,
		personIndexes: number[] | undefined,
		peoplePart: PeopleMaskPart,
		generation: number,
	) {
		const selectedPersonIndexes = getSelectedPersonIndexes(
			segmentation,
			personIndexes,
			peoplePart,
		)
		const selectedInstances = selectedPersonIndexes
			.map((personIndex) =>
				segmentation.persons.find((item) => item.personIndex === personIndex),
			)
			.filter((instance) => instance !== undefined)
		if (selectedInstances.length === 0) {
			return null
		}

		const result = {
			mask: new Float32Array(segmentation.imageSpace.width * segmentation.imageSpace.height),
			width: segmentation.imageSpace.width,
			height: segmentation.imageSpace.height,
		}
		for (const instance of selectedInstances) {
			this.assertCurrentGeneration(generation)
			const instanceMask = await this.getPeoplePartMask(
				photo,
				segmentation,
				instance,
				peoplePart,
			)
			this.assertCurrentGeneration(generation)
			if (instanceMask) {
				mergeMask(result.mask, instanceMask)
			}
		}
		return result
	}

	private async getPeoplePartMask(
		photo: Photo,
		segmentation: PeopleSegmentationResult,
		instance: SegmentedPerson,
		peoplePart: PeopleMaskPart,
	) {
		if (peoplePart === "entire") {
			return instance.mask
		}
		if (peoplePart === "facialSkin") {
			return await this.getCompositeFacialSkinMask(photo, segmentation, instance)
		}
		if (isFaceParsingPart(peoplePart)) {
			return await this.getFacePartMask(photo, segmentation, instance, peoplePart)
		}
		return await this.getHumanPartMask(photo, segmentation, instance, peoplePart)
	}

	private async getCompositeFacialSkinMask(
		photo: Photo,
		segmentation: PeopleSegmentationResult,
		instance: SegmentedPerson,
	) {
		const baseMask = await this.getHumanPartMask(photo, segmentation, instance, "facialSkin")
		if (!baseMask) {
			return null
		}

		const subtractParts: Exclude<FaceParsingPart, "entire" | "facialSkin">[] = [
			"eyebrows",
			"eyes",
			"lips",
		]
		for (const part of subtractParts) {
			const subtractMask = await this.getFacePartMask(photo, segmentation, instance, part)
			if (!subtractMask) {
				continue
			}
			for (let index = 0; index < baseMask.length; index += 1) {
				if ((subtractMask[index] ?? 0) > 0) {
					baseMask[index] = 0
				}
			}
		}

		return baseMask
	}

	private async getHumanPartMask(
		photo: Photo,
		segmentation: PeopleSegmentationResult,
		instance: SegmentedPerson,
		peoplePart: SchpHumanParsingPart,
	) {
		const parsing = await this.getHumanParsing(photo, instance)

		return projectSchpPartMask({
			partMask: createSchpPartMask(parsing.classMask, peoplePart),
			partWidth: parsing.width,
			partHeight: parsing.height,
			crop: parsing.crop,
			personMask: instance.mask,
			outputWidth: segmentation.imageSpace.width,
			outputHeight: segmentation.imageSpace.height,
		})
	}

	private async getFacePartMask(
		photo: Photo,
		segmentation: PeopleSegmentationResult,
		instance: SegmentedPerson,
		peoplePart: Exclude<FaceParsingPart, "entire">,
	) {
		if (instance.matchedFaceIndex === undefined) {
			return null
		}

		const face = segmentation.faces[instance.matchedFaceIndex]
		if (!face) {
			return null
		}

		const parsing = await this.getFaceParsing(
			photo,
			instance.personIndex,
			getFaceParsingSourceRect(face),
		)

		return projectFaceParsingPartMask({
			partMask: createFaceParsingPartMask(parsing.classMask, peoplePart),
			partWidth: parsing.width,
			partHeight: parsing.height,
			crop: parsing.crop,
			outputWidth: segmentation.imageSpace.width,
			outputHeight: segmentation.imageSpace.height,
		})
	}

	private async getFaceParsing(photo: Photo, personIndex: number, faceRect: Rect) {
		const id = `${this.getPeopleSegmentationId(photo)}_person-${personIndex}_face-parser-uniface-int8`
		const cached = this.inFlightFaceParsing.get(id)
		if (cached) {
			return cached
		}

		const generation = this.generation
		const promise = this.computeFaceParsing(photo, personIndex, faceRect, generation).finally(
			() => {
				if (this.inFlightFaceParsing.get(id) === promise) {
					this.inFlightFaceParsing.delete(id)
				}
			},
		)
		this.inFlightFaceParsing.set(id, promise)
		return promise
	}

	private async getHumanParsing(photo: Photo, instance: SegmentedPerson) {
		const id = `${this.getPeopleSegmentationId(photo)}_person-${instance.personIndex}_schp-cihp-global-int8`
		const cached = this.inFlightHumanParsing.get(id)
		if (cached) {
			return cached
		}

		const generation = this.generation
		const promise = this.computeHumanParsing(photo, instance, generation).finally(() => {
			if (this.inFlightHumanParsing.get(id) === promise) {
				this.inFlightHumanParsing.delete(id)
			}
		})
		this.inFlightHumanParsing.set(id, promise)
		return promise
	}

	private async computeHumanParsing(
		photo: Photo,
		instance: SegmentedPerson,
		generation = this.generation,
	) {
		// SCHP is cached as one child bundle per person. Its single class-map chunk contains
		// every semantic class; individual body-part masks are derived only when requested.
		const parentBundleId = this.getPeopleSegmentationId(photo)
		const descriptor = getArtifactDescriptor(
			this.cache,
			photo,
			"human-classes",
			"human-schp-v1",
			{
				parentBundleId,
				personIndex: instance.personIndex,
			},
		)
		if (descriptor) {
			const cached = await this.cache?.readArtifactBundle(descriptor.id, ["classes"])
			this.assertCurrentGeneration(generation)
			const chunk = cached?.chunks[0]
			if (chunk) {
				const metadata = cached.bundle.metadata as unknown as Omit<
					SchpHumanParsingResult,
					"classMask"
				>
				return { ...metadata, classMask: new Uint8Array(chunk.data) }
			}
		}
		const input = await this.getPeopleSegmentationInput(photo)
		this.assertCurrentGeneration(generation)
		if (!input) {
			throw new Error("Could not create human parsing input")
		}

		const result = await this.proxy.getHumanParsing(input, instance.box)
		this.assertCurrentGeneration(generation)
		if (!result) {
			throw new Error("Could not parse person")
		}
		const { classMask, ...metadata } = result
		await writeArtifact(
			this.cache,
			descriptor,
			metadata as unknown as Record<string, unknown>,
			[
				createArtifactChunk(
					descriptor?.id ?? "memory:human-classes",
					"classes",
					result.width,
					result.height,
					classMask,
					"u8-class-map-v1",
				),
			],
		)
		return result
	}

	private async computeFaceParsing(
		photo: Photo,
		personIndex: number,
		faceRect: Rect,
		generation = this.generation,
	) {
		// UniFace follows the same per-person child-bundle layout as SCHP.
		const thresholds = getPersonMaskThresholds()
		const parentBundleId = this.getPeopleSegmentationId(photo)
		const descriptor = getArtifactDescriptor(
			this.cache,
			photo,
			"face-classes",
			"face-uniface-v1",
			{
				parentBundleId,
				personIndex,
				settingsDigest: `${thresholds.faceParser}`,
			},
		)
		if (descriptor) {
			const cached = await this.cache?.readArtifactBundle(descriptor.id, ["classes"])
			this.assertCurrentGeneration(generation)
			const chunk = cached?.chunks[0]
			if (chunk) {
				const metadata = cached.bundle.metadata as unknown as Omit<
					FaceParsingResult,
					"classMask"
				>
				return { ...metadata, classMask: new Uint8Array(chunk.data) }
			}
		}
		const input = await this.getFaceParsingInput(photo)
		this.assertCurrentGeneration(generation)
		if (!input) {
			throw new Error("Could not create face parsing input")
		}

		const result = await this.proxy.getFaceParsing(input, faceRect, thresholds.faceParser)
		this.assertCurrentGeneration(generation)
		if (!result) {
			throw new Error("Could not parse face")
		}
		const { classMask, ...metadata } = result
		await writeArtifact(
			this.cache,
			descriptor,
			metadata as unknown as Record<string, unknown>,
			[
				createArtifactChunk(
					descriptor?.id ?? "memory:face-classes",
					"classes",
					result.width,
					result.height,
					classMask,
					"u8-class-map-v1",
				),
			],
		)
		return result
	}

	private async precomputePeopleParts(
		photo: Photo,
		segmentation: PeopleSegmentationResult,
		generation = this.generation,
	) {
		this.assertCurrentGeneration(generation)

		for (const instance of segmentation.persons) {
			// Process people sequentially to bound model execution and temporary memory. Parsers for
			// the current person may run together because they use separate singleton models.
			this.assertCurrentGeneration(generation)
			const tasks: Array<Promise<unknown>> = [this.getHumanParsing(photo, instance)]

			if (instance.matchedFaceIndex !== undefined) {
				const face = segmentation.faces[instance.matchedFaceIndex]
				if (face) {
					const faceRect = getFaceParsingSourceRect(face)
					tasks.push(this.getFaceParsing(photo, instance.personIndex, faceRect))
				}
			}

			await Promise.all(tasks)
		}
	}

	private async precomputePeoplePartsFromMetadata(
		photo: Photo,
		metadata: StoredPeopleSegmentationMetadata,
		generation = this.generation,
	) {
		this.assertCurrentGeneration(generation)
		for (const person of metadata.persons) {
			this.assertCurrentGeneration(generation)
			const instance = { ...person, mask: new Float32Array(0) }
			const tasks: Array<Promise<unknown>> = [this.getHumanParsing(photo, instance)]
			if (person.matchedFaceIndex !== undefined) {
				const face = metadata.faces[person.matchedFaceIndex]
				if (face) {
					const faceRect = getFaceParsingSourceRect(face)
					tasks.push(this.getFaceParsing(photo, person.personIndex, faceRect))
				}
			}
			await Promise.all(tasks)
			this.assertCurrentGeneration(generation)
		}
	}

	private async computePeopleSegmentation(
		photo: Photo,
		generation = this.generation,
	): Promise<PeopleSegmentationResult | null> {
		const descriptor = this.getPeopleSegmentationDescriptor(photo)
		let segmentation: PeopleSegmentationResult | null = null
		if (descriptor) {
			const cached = await this.cache?.readArtifactBundle(descriptor.id)
			if (cached) {
				segmentation = restorePeopleSegmentation(cached.bundle, cached.chunks)
				if (!segmentation) {
					await this.cache
						?.deleteArtifact(cached.bundle.id)
						.catch(Sentry.captureException)
				}
			}
		}

		if (!segmentation) {
			const input = await this.getPeopleSegmentationInput(photo)
			if (!input) {
				throw new Error("Could not create people segmentation input")
			}
			const thresholds = getPersonMaskThresholds()
			const result = await this.proxy.getPeopleSegmentation(input, {
				faceDetectorConfidenceThreshold: thresholds.faceDetector,
				rfdetrPersonConfidenceThreshold: thresholds.rfdetrPerson,
			})
			if (!result) {
				return null
			}
			const canonical = canonicalizePeopleSegmentation(descriptor?.id, result)
			segmentation = canonical.segmentation
			// Persist RF-DETR instance segmentation and face-matching metadata before parser work.
			// This is independent of whether the user later selects or creates a mask group.
			await writeArtifact(
				this.cache,
				descriptor,
				canonical.metadata as unknown as Record<string, unknown>,
				canonical.chunks,
			)
		}
		this.assertCurrentGeneration(generation)
		await this.precomputePeopleParts(photo, segmentation, generation)
		this.assertCurrentGeneration(generation)

		return segmentation
	}

	private async setPeopleMaskDetectionCoord(
		photo: Photo,
		mask: PeopleMask,
		personIndexes: number[] | undefined,
		result?: MaskResult,
	) {
		const resultCenter = getMaskResultCenter(result)
		if (resultCenter) {
			this.maskDetectionCoords[mask.id] = toNoOrientation(
				resultCenter,
				photo.initialOrientation,
			)
			this.onDetectionCoordinatesChanged?.(mask)
			return
		}

		if (personIndexes?.length !== 1) {
			return
		}
		const segmentation = await this.getPeopleSegmentation(photo)
		const instance = segmentation?.persons.find((item) => item.personIndex === personIndexes[0])
		const input = this.peopleSegmentationInput
		if (!instance || !input) {
			return
		}

		const x = clamp((instance.box.xMin + instance.box.width / 2) / input.width, 0, 1)
		const y = clamp((instance.box.yMin + instance.box.height / 2) / input.height, 0, 1)
		this.maskDetectionCoords[mask.id] = toNoOrientation([x, y], photo.initialOrientation)
		this.onDetectionCoordinatesChanged?.(mask)
	}

	private async getPeopleSegmentationInput(photo: Photo) {
		if (this.peopleSegmentationInput) {
			return this.peopleSegmentationInput
		}

		const size = fitToSize(getInitialOrientedImageSize(photo), [1024, 1024])
		const input = await this.renderAnalysisInput(photo, size)
		this.peopleSegmentationInput = input ?? null
		return this.peopleSegmentationInput
	}

	private async getFaceParsingInput(photo: Photo) {
		if (this.faceParsingInput) {
			return this.faceParsingInput
		}

		const size = fitToSize(getInitialOrientedImageSize(photo), [2048, 2048])
		const input = await this.renderAnalysisInput(photo, size)
		this.faceParsingInput = input ?? null
		return this.faceParsingInput
	}

	private getPeopleSegmentationId(photo: Photo) {
		const thresholds = getPersonMaskThresholds()
		const descriptor = this.getPeopleSegmentationDescriptor(photo)
		if (descriptor) {
			return descriptor.id
		}
		return `${photo.id}_people-segmentation_rfdetr-seg-nano-uint8_${getPersonMaskThresholdKey(thresholds)}`
	}

	private getPeopleSegmentationDescriptor(photo: Photo) {
		const thresholds = getPersonMaskThresholds()
		return getArtifactDescriptor(
			this.cache,
			photo,
			"people-segmentation",
			"people-rfdetr-uint8-v1",
			{
				settingsDigest: getPersonMaskThresholdKey(thresholds),
			},
		)
	}

	private createPeopleThumbnail(
		input: ImageBitmap,
		instance: Pick<RfDetrSegmentedPerson, "box">,
		faceRect: Rect | undefined,
	) {
		if (typeof document === "undefined") {
			return undefined
		}

		const rect =
			faceRect ??
			({
				x: instance.box.xMin / input.width,
				y: instance.box.yMin / input.height,
				width: instance.box.width / input.width,
				height: instance.box.height / input.height,
			} satisfies Rect)

		const centerX = (rect.x + rect.width / 2) * input.width
		const centerY = (rect.y + rect.height / 2) * input.height
		const sourceSize = Math.max(rect.width * input.width, rect.height * input.height) * 1.3
		const size = Math.max(16, Math.min(input.width, input.height, sourceSize))
		const sourceX = Math.max(0, Math.min(input.width - size, centerX - size / 2))
		const sourceY = Math.max(0, Math.min(input.height - size, centerY - size / 2))
		const canvas = document.createElement("canvas")
		canvas.width = 160
		canvas.height = 160
		const context = canvas.getContext("2d")
		if (!context) {
			return undefined
		}

		context.drawImage(input, sourceX, sourceY, size, size, 0, 0, canvas.width, canvas.height)
		return canvas.toDataURL("image/png")
	}
}

function getMaskPersonIndexes(mask: PeopleMask) {
	if (mask.personIndexes && mask.personIndexes.length > 0) {
		return mask.personIndexes
	}

	if (mask.personIndex !== undefined) {
		return [mask.personIndex]
	}

	return undefined
}

function canonicalizePeopleSegmentation(
	bundleId: string | undefined,
	result: RfDetrPeopleSegmentationResult,
) {
	const id = bundleId ?? "memory:people-segmentation"
	const chunks: AIMaskArtifactChunkRecord[] = []
	// RF-DETR instances are separate roles in one bundle; unlike parser class maps, each person
	// must be independently retrievable for visibility checks and user selection.
	const persons = result.persons.map(({ mask, ...person }) => {
		const canonical = canonicalizeAlphaMask(id, `person:${person.personIndex}`, {
			mask,
			width: result.imageSpace.width,
			height: result.imageSpace.height,
		})
		chunks.push(canonical.chunk)
		return { metadata: person, mask: canonical.result.mask }
	})
	const metadata: StoredPeopleSegmentationMetadata = {
		imageSpace: result.imageSpace,
		persons: persons.map(({ metadata: person }) => person),
		faces: result.faces,
		faceMatches: result.faceMatches,
	}
	const segmentation: PeopleSegmentationResult = {
		...metadata,
		persons: persons.map(({ metadata: person, mask }) => ({ ...person, mask })),
	}
	return { segmentation, metadata, chunks }
}

function isStoredPeopleSegmentationMetadata(
	value: unknown,
	chunkRoles: unknown,
): value is StoredPeopleSegmentationMetadata {
	if (!value || typeof value !== "object") {
		return false
	}
	const metadata = value as Partial<StoredPeopleSegmentationMetadata>
	const imageSpace = metadata.imageSpace
	if (
		!imageSpace ||
		!Number.isFinite(imageSpace.width) ||
		imageSpace.width <= 0 ||
		!Number.isFinite(imageSpace.height) ||
		imageSpace.height <= 0 ||
		!Array.isArray(metadata.persons) ||
		!Array.isArray(metadata.faces) ||
		!Array.isArray(metadata.faceMatches) ||
		!Array.isArray(chunkRoles)
	) {
		return false
	}

	const personIndexes = new Set<number>()
	for (const person of metadata.persons) {
		if (
			!person ||
			!Number.isInteger(person.personIndex) ||
			person.personIndex < 0 ||
			!Number.isInteger(person.queryIndex) ||
			!Number.isFinite(person.score) ||
			!person.box ||
			!Number.isFinite(person.box.xMin) ||
			!Number.isFinite(person.box.yMin) ||
			!Number.isFinite(person.box.width) ||
			!Number.isFinite(person.box.height) ||
			person.box.width <= 0 ||
			person.box.height <= 0 ||
			personIndexes.has(person.personIndex)
		) {
			return false
		}
		personIndexes.add(person.personIndex)
	}

	return (
		chunkRoles.length === personIndexes.size &&
		chunkRoles.every((role) => typeof role === "string") &&
		[...personIndexes].every((personIndex) => chunkRoles.includes(`person:${personIndex}`))
	)
}

function restorePeopleSegmentation(
	bundle: AIMaskArtifactBundleRecord,
	chunks: AIMaskArtifactChunkRecord[],
): PeopleSegmentationResult | null {
	if (!isStoredPeopleSegmentationMetadata(bundle.metadata, bundle.chunkRoles)) {
		return null
	}
	const metadata = bundle.metadata
	const persons: SegmentedPerson[] = []
	for (const person of metadata.persons) {
		const chunk = chunks.find(({ role }) => role === `person:${person.personIndex}`)
		if (!chunk) {
			return null
		}
		persons.push({ ...person, mask: decodeAlphaMask(chunk).mask })
	}
	return {
		imageSpace: metadata.imageSpace,
		persons,
		faces: metadata.faces,
		faceMatches: metadata.faceMatches,
	}
}

function getSelectedPersonIndexes(
	segmentation: { persons: Array<{ personIndex: number }> },
	personIndexes: number[] | undefined,
	peoplePart: PeopleMaskPart,
) {
	return personIndexes?.length || peoplePart !== "entire"
		? (personIndexes ?? [])
		: segmentation.persons.map(({ personIndex }) => personIndex)
}

function mergeMask(destination: Float32Array, source: Float32Array) {
	for (let index = 0; index < destination.length; index += 1) {
		destination[index] = Math.max(destination[index] ?? 0, source[index] ?? 0)
	}
}

function getPeopleMaskSelectionKey(mask: PeopleMask) {
	const personIndexes = getMaskPersonIndexes(mask)
	const peoplePart = mask.peoplePart ?? "entire"
	return `${peoplePart}_${personIndexes?.join("-") ?? "all"}`
}

function isFaceParsingPart(
	peoplePart: PeopleMaskPart,
): peoplePart is Exclude<FaceParsingPart, "entire"> {
	return peoplePart === "eyebrows" || peoplePart === "eyes" || peoplePart === "lips"
}

function createEmptyPeopleMaskPartAvailability(): PeopleMaskPartAvailability {
	return {
		facialSkin: false,
		eyebrows: false,
		eyes: false,
		lips: false,
		clothing: false,
		bodySkin: false,
		hair: false,
	}
}

function hasMaskPixels(mask: Float32Array) {
	for (const value of mask) {
		if (value > 0) {
			return true
		}
	}
	return false
}

function maskIntersectsVisibleImage(
	mask: Float32Array,
	width: number,
	height: number,
	cropMatrix: mat4,
	initialOrientation: number,
) {
	const displayMatrix = mat4.invert(mat4.create(), cropMatrix)
	if (!displayMatrix) {
		return true
	}

	for (let index = 0; index < mask.length; index += 1) {
		if ((mask[index] ?? 0) <= 0.01) {
			continue
		}

		const x = (index % width) + 0.5
		const y = Math.floor(index / width) + 0.5
		const [sourceX, sourceY] = toNoOrientation([x / width, y / height], initialOrientation)
		const point = vec4.fromValues(sourceX * 2 - 1, sourceY * 2 - 1, 0, 1)
		vec4.transformMat4(point, point, displayMatrix)

		const w = point[3] || 1
		const displayX = point[0] / w
		const displayY = point[1] / w
		if (displayX >= -1 && displayX <= 1 && displayY >= -1 && displayY <= 1) {
			return true
		}
	}

	return false
}

function getPersonMaskThresholds() {
	return {
		faceDetector: getConfidenceThreshold(faceDetectorConfidenceThresholdAtom, 0.55),
		faceParser: getConfidenceThreshold(faceParserConfidenceThresholdAtom, 0),
		rfdetrPerson: getConfidenceThreshold(rfdetrPersonConfidenceThresholdAtom, 0.5),
	}
}

function getPersonMaskThresholdKey(thresholds = getPersonMaskThresholds()) {
	return `fd-${thresholds.faceDetector.toFixed(2)}_rp-${thresholds.rfdetrPerson.toFixed(2)}`
}

function getConfidenceThreshold(atom: Atom<number>, fallback: number) {
	const value = store.get(atom)
	return Number.isFinite(value) ? clamp(value, 0, 1) : fallback
}

function getMaskResultCenter(result?: MaskResult): vec2 | undefined {
	if (!result || result.width <= 0 || result.height <= 0) {
		return undefined
	}

	let totalWeight = 0
	let weightedX = 0
	let weightedY = 0
	for (let index = 0; index < result.mask.length; index += 1) {
		const value = result.mask[index] ?? 0
		if (value <= 0.5) {
			continue
		}

		const x = index % result.width
		const y = Math.floor(index / result.width)
		weightedX += (x + 0.5) * value
		weightedY += (y + 0.5) * value
		totalWeight += value
	}

	if (totalWeight === 0) {
		return undefined
	}

	return [
		clamp(weightedX / totalWeight / result.width, 0, 1),
		clamp(weightedY / totalWeight / result.height, 0, 1),
	]
}

function getInitialOrientedImageSize(photo: Photo): vec2 {
	const [width, height] = photo.imageState.originalImageSize
	return Math.abs(photo.initialOrientation) % 2 ? [height, width] : [width, height]
}
